import React, { useEffect, useRef, forwardRef, useImperativeHandle, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    TextField,
    Button,
    Grid,
    Typography,
    Box,
    CircularProgress,
    Paper,
    Chip,
    Avatar
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SendIcon from '@material-ui/icons/Send';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { imagesRepo } from '../../../config';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
        backgroundColor: '#ffffff',
    },
    emptyState: {
        padding: theme.spacing(4),
        textAlign: 'center',
        backgroundColor: '#fafafa',
        borderRadius: theme.spacing(1),
        border: '1px dashed #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: theme.spacing(20),
    },
    emptyIcon: {
        fontSize: 48,
        color: '#bdbdbd',
        marginBottom: theme.spacing(2),
    },
    infoCard: {
        padding: theme.spacing(2),
        backgroundColor: '#f8f9fa',
        borderRadius: theme.spacing(1),
        border: '1px solid #e9ecef',
        marginBottom: theme.spacing(3),
    },
    infoRow: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(1),
        '&:last-child': {
            marginBottom: 0,
        },
    },
    label: {
        color: '#6c757d',
        fontSize: '0.875rem',
        marginRight: theme.spacing(1),
    },
    value: {
        color: '#212529',
        fontWeight: 500,
        fontSize: '0.875rem',
    },
    sertisseurName: {
        color: '#7b1fa2',
        fontWeight: 600,
        fontSize: '1rem',
        marginBottom: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
    },
    submitButton: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(1.5),
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.9375rem',
        borderRadius: theme.spacing(1),
        boxShadow: 'none',
        '&:hover': {
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
    },
    textField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: theme.spacing(1),
            backgroundColor: '#ffffff',
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#7b1fa2',
            },
        },
    },
}));

const validationSchema = Yup.object().shape({
    poidsR: Yup.number()
        .required('Poids retour obligatoire.')
        .typeError('Poids retour doit être un nombre.')
        .min(0, 'Le poids doit être positif.'),
    prix: Yup.number()
        .required('Prix obligatoire.')
        .typeError('Prix doit être un nombre.')
        .min(0, 'Le prix doit être positif.'),
});

const safeFieldValue = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toString() : '';
};

const formatWeight = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toFixed(2) : '0.00';
};

const SertisseurRetourModule = forwardRef(({ 
    selectedItem, 
    onSubmit,
    loading = false,
    hideSubmitButton = false
}, ref) => {
    const classes = useStyles();
    const poidsRRef = useRef(null);
    const prixRef = useRef(null);
    const [hasSubmitted, setHasSubmitted] = useState(false);

    useImperativeHandle(ref, () => ({
        focusPoidsR: () => {
            if (poidsRRef.current) {
                poidsRRef.current.focus();
                if (typeof poidsRRef.current.select === 'function') {
                    poidsRRef.current.select();
                }
            }
        }
    }));

    const formik = useFormik({
        initialValues: {
            poidsR: '',
            prix: '',
        },
        validationSchema,
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: (values) => {
            onSubmit(values);
        },
    });

    const { values, errors, handleChange, handleSubmit, resetForm } = formik;

    const handleFormSubmit = (event) => {
        if (event) {
            event.preventDefault();
        }
        setHasSubmitted(true);
        handleSubmit(event);
    };

    useEffect(() => {
        if (!selectedItem) {
            resetForm();
            setHasSubmitted(false);
            return;
        }

        const isReturned = Number(selectedItem.rendu_serti) === 1;
        if (isReturned) {
            resetForm({
                values: {
                    poidsR: safeFieldValue(selectedItem.pds_r),
                    prix: safeFieldValue(selectedItem.prix),
                },
            });
        } else {
            resetForm();
        }

        setHasSubmitted(false);
    }, [resetForm, selectedItem]);

    const handleKeyPress = async (event, nextRef) => {
        if (event.key !== 'Enter') {
            return;
        }

        event.preventDefault();
        setHasSubmitted(true);

        const fieldName = event.target.name;
        await formik.validateField(fieldName);
        const fieldMeta = formik.getFieldMeta(fieldName);

        if (fieldMeta.error) {
            return;
        }

        if (nextRef?.current) {
            nextRef.current.focus();
            if (typeof nextRef.current.select === 'function') {
                nextRef.current.select();
            }
            return;
        }

        formik.submitForm();
    };

    return (
        <Box className={classes.root}>
            {selectedItem ? (
                <form onSubmit={handleFormSubmit}>
                    <Grid container spacing={2} style={{ minHeight: 'auto' }}>
                        <Grid item xs={12} sm={6}>
                            <Paper className={classes.infoCard} elevation={0}>
                                <Grid container spacing={2}>
                                    <Grid item xs={3} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Avatar 
                                            src={selectedItem.avatar ? `${imagesRepo}/avatar/${selectedItem.avatar}` : undefined}
                                            alt={selectedItem.label}
                                            style={{ 
                                                width: 60, 
                                                height: 60,
                                                backgroundColor: '#7b1fa2',
                                                fontSize: '1.5rem',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {!selectedItem.avatar && selectedItem.label ? selectedItem.label.charAt(0).toUpperCase() : '?'}
                                        </Avatar>
                                    </Grid>
                                    <Grid item xs={9}>
                                        <Typography className={classes.sertisseurName}>
                                            {selectedItem.label}
                                        </Typography>

                                        <Box className={classes.infoRow}>
                                            <Typography className={classes.label}>N° OF:</Typography>
                                            <Chip 
                                                label={selectedItem.of_serto} 
                                                size="small" 
                                                color="primary"
                                                style={{ fontWeight: 500 }}
                                            />
                                        </Box>

                                        <Box className={classes.infoRow}>
                                            <Typography className={classes.label}>Poids départ:</Typography>
                                            <Typography className={classes.value}>
                                                {formatWeight(selectedItem.pds_d)} g
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Box>
                                <TextField
                                    fullWidth
                                    label="Poids retour"
                                    type="number"
                                    inputProps={{ step: '0.01', min: '0' }}
                                    name="poidsR"
                                    value={values.poidsR}
                                    onChange={handleChange}
                                    onKeyPress={(event) => handleKeyPress(event, prixRef)}
                                    error={hasSubmitted && !!errors.poidsR}
                                    helperText={hasSubmitted && errors.poidsR ? errors.poidsR : ' '}
                                    variant="outlined"
                                    disabled={loading}
                                    inputRef={poidsRRef}
                                    className={classes.textField}
                                    size="small"
                                    style={{ marginBottom: 16 }}
                                />

                                <TextField
                                    fullWidth
                                    label="Prix"
                                    type="number"
                                    inputProps={{ step: '0.01', min: '0' }}
                                    name="prix"
                                    value={values.prix}
                                    onChange={handleChange}
                                    onKeyPress={(event) => handleKeyPress(event, null)}
                                    error={hasSubmitted && !!errors.prix}
                                    helperText={hasSubmitted && errors.prix ? errors.prix : ' '}
                                    variant="outlined"
                                    disabled={loading}
                                    inputRef={prixRef}
                                    className={classes.textField}
                                    size="small"
                                    style={{ marginBottom: 16 }}
                                />

                                {!hideSubmitButton && (
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        disabled={loading || !selectedItem}
                                        className={classes.submitButton}
                                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                                        style={{ 
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            fontSize: '0.875rem',
                                            borderRadius: '8px',
                                            padding: '8px 16px',
                                            boxShadow: 'none'
                                        }}
                                    >
                                        {loading ? 'Enregistrement...' : 'Enregistrer le retour'}
                                    </Button>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            ) : (
                <Box className={classes.emptyState}>
                    <InfoOutlinedIcon className={classes.emptyIcon} />
                    <Typography variant="body1" color="textSecondary">
                        Sélectionnez un mouvement
                    </Typography>
                    <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
                        Cliquez sur une ligne pour enregistrer le retour
                    </Typography>
                </Box>
            )}
        </Box>
    );
});

SertisseurRetourModule.displayName = 'SertisseurRetourModule';

export default SertisseurRetourModule;
